import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getMe, type MeFaction, type MeResponse, type MeUser } from "../lib/api";
import "./Onboarding.css";

// Multi-step onboarding wizard.
//
// Steps:
//   1. Identity confirm   — show avatar + display name + handle, "Continue".
//   2. Role               — creator / donor cards.
//   3. Setup (creator)    — bio + faction rename (skippable).
//   4. Drop your totem    — overlay URL + OBS hint + skip to dashboard.
//
// Auto-skip: if user is already a creator with a streamer row + factions +
// onboarded_at, jump straight to /dashboard.

type Step = 1 | 2 | 3 | 4;

const PLACEHOLDER_BIO = "Streaming and chasing factions every day";

export default function Onboarding(): JSX.Element {
  const nav = useNavigate();
  const qc = useQueryClient();

  const me = useQuery({ queryKey: ["me"], queryFn: getMe, staleTime: 30_000 });
  const user = me.data?.user;
  const streamer = me.data?.streamer ?? null;
  const remoteFactions = useMemo(() => me.data?.factions ?? [], [me.data]);

  const [step, setStep] = useState<Step>(1);
  const [role, setRole] = useState<"creator" | "donor" | null>(null);
  const [bio, setBio] = useState<string>("");
  const [factions, setFactions] = useState<MeFaction[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Hydrate editable state from /api/me once it loads.
  useEffect(() => {
    if (streamer && bio === "") setBio(streamer.bio ?? "");
    if (remoteFactions.length && factions.length === 0) setFactions(remoteFactions);
  }, [streamer, remoteFactions, bio, factions.length]);

  // Auto-skip already-onboarded users.
  useEffect(() => {
    if (!me.data) return;
    const u = me.data.user;
    if (!u) {
      nav("/login?returnUrl=/onboarding", { replace: true });
      return;
    }
    if (u.is_creator && streamer && remoteFactions.length >= 1 && u.onboarded_at) {
      nav("/dashboard", { replace: true });
    }
  }, [me.data, streamer, remoteFactions, nav]);

  if (me.isLoading || !user) {
    return (
      <section className="card stack ob-v5" style={{ maxWidth: 560, margin: "60px auto", padding: 28 }}>
        <p>Loading your onboarding…</p>
      </section>
    );
  }

  const hasChannel = !!user.twitch_linked || !!user.youtube_channel_id;

  async function finishDonor(): Promise<void> {
    setSubmitting(true);
    setErr(null);
    try {
      await api.post<{ ok: true }>("/api/me/onboarding", { role: "donor" });
      await qc.invalidateQueries({ queryKey: ["me"] });
      nav("/", { replace: true });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function finishCreator(): Promise<void> {
    setSubmitting(true);
    setErr(null);
    try {
      await api.post<MeResponse & { ok: true }>("/api/me/onboarding", {
        role: "creator",
        bio: bio.trim(),
        factions: factions.map((f) => ({ slug: f.slug, name: f.name, color: f.color })),
      });
      await qc.invalidateQueries({ queryKey: ["me"] });
      setStep(4);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className="card stack ob-v5"
      style={{ maxWidth: 620, margin: "60px auto", padding: 28 }}
    >
      <Stepper step={step} role={role} />

      {step === 1 && (
        <StepIdentity
          user={user}
          hasChannel={hasChannel}
          onContinue={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <StepRole
          onCreator={() => {
            setRole("creator");
            setStep(3);
          }}
          onDonor={async () => {
            setRole("donor");
            await finishDonor();
          }}
          onBack={() => setStep(1)}
          submitting={submitting}
        />
      )}

      {step === 3 && (
        <StepSetup
          bio={bio}
          setBio={setBio}
          factions={factions.length ? factions : remoteFactions}
          setFactions={setFactions}
          onBack={() => setStep(2)}
          onContinue={finishCreator}
          submitting={submitting}
        />
      )}

      {step === 4 && (
        <StepLaunch
          overlayKey={
            (qc.getQueryData<MeResponse>(["me"]) ?? me.data)?.streamer?.overlay_key ??
            streamer?.overlay_key ??
            ""
          }
          handle={user.handle}
          onDone={() => nav("/dashboard", { replace: true })}
        />
      )}

      {err ? <p style={{ color: "var(--danger, #ff5252)" }}>{err}</p> : null}
    </section>
  );
}

// ---------- Stepper ----------

function Stepper({ step, role }: { step: Step; role: "creator" | "donor" | null }): JSX.Element {
  const labels = role === "donor"
    ? ["Welcome", "Role"]
    : ["Welcome", "Role", "Setup", "Launch"];
  return (
    <div className="ob-stepper">
      {labels.map((label, i) => {
        const n = (i + 1) as Step;
        const active = n === step;
        const done = n < step;
        return (
          <div key={label} className={`ob-step ${active ? "act" : ""} ${done ? "done" : ""}`}>
            <span className="ob-step-dot">{done ? "✓" : n}</span>
            <span className="ob-step-label">{label}</span>
          </div>
        );
      })}
      <style>{`
        .ob-stepper { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 6px; }
        .ob-step { display: inline-flex; align-items: center; gap: 8px; color: var(--text-mute); font-family: var(--f-mono); font-size: 11px; letter-spacing: .12em; text-transform: uppercase; }
        .ob-step-dot { width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--line); display: inline-flex; align-items: center; justify-content: center; font-size: 11px; }
        .ob-step.act { color: var(--text-pri); }
        .ob-step.act .ob-step-dot { border-color: var(--accent, #FF1744); color: var(--accent, #FF1744); }
        .ob-step.done .ob-step-dot { background: var(--accent, #FF1744); color: #fff; border-color: var(--accent, #FF1744); }
      `}</style>
    </div>
  );
}

// ---------- Step 1: Identity ----------

function StepIdentity({
  user,
  hasChannel,
  onContinue,
}: {
  user: MeUser;
  hasChannel: boolean;
  onContinue: () => void;
}): JSX.Element {
  const platform = user.twitch_linked
    ? { label: "Twitch", color: "#9146FF" }
    : user.google_linked
      ? { label: user.youtube_channel_id ? "YouTube" : "Google", color: "#FF0000" }
      : { label: "Email", color: "var(--text-mute)" };
  return (
    <>
      <h1 style={{ margin: 0 }}>Welcome, {user.display_name}!</h1>
      <p style={{ color: "var(--text-sec)", marginTop: 4 }}>
        This is how the world will see you on SubsWall.
      </p>
      <div className="ob-card">
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="" className="ob-avatar" />
        ) : (
          <div className="ob-avatar ob-avatar-fallback">{user.display_name.charAt(0)}</div>
        )}
        <div className="stack" style={{ gap: 4 }}>
          <strong style={{ fontSize: 18 }}>{user.display_name}</strong>
          <span style={{ color: "var(--text-mute)", fontFamily: "var(--f-mono)", fontSize: 12 }}>
            subswall.com/{user.handle}
          </span>
          <span
            className="ob-pill"
            style={{ borderColor: platform.color, color: platform.color }}
          >
            {platform.label}
          </span>
        </div>
      </div>
      {!hasChannel && (
        <p style={{ color: "var(--text-mute)", fontSize: 13 }}>
          We didn't find a streaming channel attached. You can still continue as a
          donor or come back after linking Twitch / YouTube.
        </p>
      )}
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button className="btn btn-primary" onClick={onContinue}>
          Continue →
        </button>
      </div>
      <style>{`
        .ob-card { display: flex; gap: 16px; align-items: center; padding: 16px; border: 1px solid var(--line); border-radius: 8px; background: color-mix(in srgb, var(--bg-card) 90%, transparent); }
        .ob-avatar { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; }
        .ob-avatar-fallback { background: var(--accent, #FF1744); color: #fff; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 24px; }
        .ob-pill { display: inline-block; font-family: var(--f-mono); font-size: 10px; letter-spacing: .14em; padding: 3px 7px; border: 1px solid currentColor; border-radius: 3px; width: fit-content; text-transform: uppercase; }
      `}</style>
    </>
  );
}

// ---------- Step 2: Role ----------

function StepRole({
  onCreator,
  onDonor,
  onBack,
  submitting,
}: {
  onCreator: () => void;
  onDonor: () => void | Promise<void>;
  onBack: () => void;
  submitting: boolean;
}): JSX.Element {
  return (
    <>
      <h1 style={{ margin: 0 }}>How will you use SubsWall?</h1>
      <p style={{ color: "var(--text-sec)", marginTop: 4 }}>
        You can change this later.
      </p>
      <div className="ob-roles">
        <button className="ob-role" onClick={onCreator} disabled={submitting}>
          <div className="ob-role-icon" aria-hidden>★</div>
          <strong>I'm a Creator</strong>
          <span>Set up a wall, add the OBS overlay, accept totems on stream.</span>
        </button>
        <button className="ob-role" onClick={onDonor} disabled={submitting}>
          <div className="ob-role-icon" aria-hidden>♥</div>
          <strong>I'm a Donor only</strong>
          <span>I just want to drop totems on streamers' walls.</span>
        </button>
      </div>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <button className="btn" onClick={onBack} disabled={submitting}>
          ← Back
        </button>
        <span style={{ color: "var(--text-mute)", fontSize: 12, alignSelf: "center" }}>
          {submitting ? "Saving…" : ""}
        </span>
      </div>
      <style>{`
        .ob-roles { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 540px) { .ob-roles { grid-template-columns: 1fr; } }
        .ob-role { text-align: left; padding: 18px; border: 1px solid var(--line); border-radius: 10px; background: var(--bg-card); color: var(--text-pri); cursor: pointer; transition: border-color .15s, transform .15s; display: flex; flex-direction: column; gap: 6px; font: inherit; }
        .ob-role:hover:not(:disabled) { border-color: var(--accent, #FF1744); transform: translateY(-1px); }
        .ob-role:disabled { opacity: .6; cursor: not-allowed; }
        .ob-role-icon { font-size: 28px; color: var(--accent, #FF1744); }
        .ob-role span { color: var(--text-sec); font-size: 13px; }
      `}</style>
    </>
  );
}

// ---------- Step 3: Setup ----------

function StepSetup({
  bio,
  setBio,
  factions,
  setFactions,
  onBack,
  onContinue,
  submitting,
}: {
  bio: string;
  setBio: (s: string) => void;
  factions: MeFaction[];
  setFactions: (f: MeFaction[]) => void;
  onBack: () => void;
  onContinue: () => void;
  submitting: boolean;
}): JSX.Element {
  return (
    <>
      <h1 style={{ margin: 0 }}>Set up your wall</h1>
      <p style={{ color: "var(--text-sec)", marginTop: 4 }}>
        Both fields are optional — you can polish in the dashboard later.
      </p>

      <label className="stack" style={{ gap: 6 }}>
        <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>
          Bio
        </span>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value.slice(0, 140))}
          maxLength={140}
          placeholder={PLACEHOLDER_BIO}
          rows={3}
          className="ob-input"
          style={{ resize: "vertical" }}
        />
        <span style={{ alignSelf: "flex-end", color: "var(--text-mute)", fontSize: 11 }}>
          {bio.length}/140
        </span>
      </label>

      <div className="stack" style={{ gap: 6 }}>
        <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>
          Factions
        </span>
        <p style={{ color: "var(--text-mute)", fontSize: 12, margin: 0 }}>
          Rename your default factions. You can edit colors later.
        </p>
        <div className="ob-factions">
          {factions.map((f, i) => (
            <div key={f.slug} className="ob-faction">
              <span className="ob-swatch" style={{ background: f.color }} />
              <input
                value={f.name}
                onChange={(e) => {
                  const next = factions.slice();
                  next[i] = { ...f, name: e.target.value.slice(0, 24) };
                  setFactions(next);
                }}
                className="ob-input"
                maxLength={24}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="row" style={{ justifyContent: "space-between" }}>
        <button className="btn" onClick={onBack} disabled={submitting}>← Back</button>
        <button className="btn btn-primary" onClick={onContinue} disabled={submitting}>
          {submitting ? "Saving…" : "Continue →"}
        </button>
      </div>

      <style>{`
        .ob-input { padding: 10px 12px; background: var(--bg-card); border: 1px solid var(--line); color: var(--text-pri); border-radius: 6px; font: inherit; font-size: 14px; width: 100%; }
        .ob-factions { display: grid; gap: 8px; }
        .ob-faction { display: flex; gap: 10px; align-items: center; }
        .ob-swatch { width: 22px; height: 22px; border-radius: 4px; flex-shrink: 0; }
      `}</style>
    </>
  );
}

// ---------- Step 4: Launch ----------

function StepLaunch({
  overlayKey,
  handle,
  onDone,
}: {
  overlayKey: string;
  handle: string;
  onDone: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const overlayUrl = overlayKey
    ? `${window.location.origin}/overlay/${overlayKey}`
    : "(loading…)";

  function copy(): void {
    if (!overlayKey) return;
    navigator.clipboard.writeText(overlayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }

  return (
    <>
      <h1 style={{ margin: 0 }}>Drop your first totem</h1>
      <p style={{ color: "var(--text-sec)", marginTop: 4 }}>
        Add this URL to OBS and you're live.
      </p>

      <div className="stack" style={{ gap: 8 }}>
        <code className="ob-url">{overlayUrl}</code>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-primary" onClick={copy} disabled={!overlayKey}>
            {copied ? "COPIED ✓" : "COPY URL"}
          </button>
          <a className="btn" href={overlayUrl} target="_blank" rel="noreferrer">
            PREVIEW ↗
          </a>
        </div>
      </div>

      <div className="card" style={{ background: "color-mix(in srgb, var(--bg-card) 80%, transparent)" }}>
        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7, fontSize: 13 }}>
          <li>Open OBS → <strong>Sources</strong> → <strong>+</strong> → <strong>Browser</strong>.</li>
          <li>Paste the URL above; size 1920×1080.</li>
          <li>Test it with the link below.</li>
        </ol>
      </div>

      <div className="row" style={{ justifyContent: "space-between" }}>
        <a className="btn" href={`/${handle}/donate?test=1`}>
          Drop a test totem →
        </a>
        <button className="btn btn-primary" onClick={onDone}>
          Go to dashboard
        </button>
      </div>

      <style>{`
        .ob-url { display: block; padding: 12px; background: var(--bg-deep, #08080a); border: 1px solid var(--line); border-radius: 6px; font-family: var(--f-mono); font-size: 13px; word-break: break-all; color: var(--text-pri); }
      `}</style>
    </>
  );
}
